import IFlowModelCommandData, {
  IFlowModelCommandDataPascalCase
} from '@Shared.Angular/@types/core/contracts/commands/modeler/flowModelCommandData';
import IGenerateFlowModelCommand from '@Shared.Angular/@types/core/contracts/commands/modeler/generateFlowModelCommand';
import { ICreateFlowModelSummary } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/createFlowModelSummary';
import IFlowModelSchemaVersion from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSchemaVersion';
import IProcessMapNestDetails from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapNestDetails';
import IProcessMapNodeElement from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapNodeElement';
import IFlowModelVersionHistory from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelVersionHistory';
import IModelerWorkflowDetail from '@Shared.Angular/@types/core/contracts/queryModel/modeler/modelerWorkflowDetail';
import IWorkflowSchemaDetail from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/workflowSchemaDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { Services } from '@Shared.Angular/@types/services';
import { IFlowModelSchema, TimeCost } from '@Shared.Angular/@types/workflow';
import angular, { IHttpResponse, IHttpService } from 'angular';

class ModelerApiService {
  static $inject = ['$http', 'APP_CONFIG', 'validationService'];
  public static stepTypes = [];
  constructor(
    private $http: IHttpService,
    private APP_CONFIG: Services.APP_CONFIG,
    private validationService: Services.ValidationService
  ) {}

  getFlowModel(workflowId) {
    return this.$http
      .get<IModelerWorkflowDetail>(
        this.APP_CONFIG.apiBaseUrl + 'modeler/findWorkflow/' + workflowId
      )
      .then((response) => {
        return response.data;
      });
  }

  getProcessMap(flowModelId: Guid, noSpinner = false) {
    return this.$http
      .get<IProcessMapNestDetails>(
        `${this.APP_CONFIG.apiBaseUrl}modeler/getProcessMap/${flowModelId}`,
        { noSpinner: noSpinner }
      )
      .then((response) => response.data);
  }

  getProcessMapbySchemaId(
    schemaId: string,
    noSpinner: boolean,
    isPublicMap = false
  ) {
    return isPublicMap
      ? this.$http
          .get<IProcessMapNestDetails>(
            `${this.APP_CONFIG.apiBaseUrl}map/schemas/${schemaId}`
          )
          .then((response) => response.data)
      : this.$http
          .get<IProcessMapNestDetails>(
            `${this.APP_CONFIG.apiBaseUrl}modeler/getProcessMapbySchemaId/${schemaId}`,
            { noSpinner: noSpinner }
          )
          .then((response) => response.data);
  }

  getProcessMapNodeElement(
    flowModelId: Guid,
    modelerNodeId: Guid,
    noSpinner: boolean,
    isPublicMap = false
  ) {
    return isPublicMap
      ? this.$http
          .get<IProcessMapNodeElement[]>(
            `${this.APP_CONFIG.apiBaseUrl}map/${flowModelId}/nodeElements/${modelerNodeId}`,
            { noSpinner: noSpinner }
          )
          .then((response) => response.data)
      : this.$http
          .get<IProcessMapNodeElement[]>(
            `${this.APP_CONFIG.apiBaseUrl}modeler/${flowModelId}/getProcessMapNodeElement/${modelerNodeId}`,
            { noSpinner: noSpinner }
          )
          .then((response) => response.data);
  }

  getSchemaByVersion(flowModelSchemaId) {
    ///
    /// Get FlowModel Schema by given version
    ///
    return this.$http.get<IFlowModelSchema>(
      this.APP_CONFIG.apiBaseUrl + 'modeler/schemaVersion/' + flowModelSchemaId
    );
  }

  getTimeCostTotal(schemaId) {
    return this.$http.get<TimeCost>(
      `${this.APP_CONFIG.apiBaseUrl}modeler/flowModelSchemas/${schemaId}/timeCost`
    );
  }

  getFlowModelSchemaVersions(workflowId) {
    return this.$http.get<IFlowModelSchemaVersion[]>(
      this.APP_CONFIG.apiBaseUrl + 'modeler/schemaVersions/' + workflowId
    );
  }

  getPublishApprovalFlowModelSchemaVersions(flowModelId) {
    return this.$http.get<IFlowModelSchemaVersion[]>(
      this.APP_CONFIG.apiBaseUrl +
        'modeler/publishApprovalsSchemaVersions/' +
        flowModelId
    );
  }

  getVersionHistory(flowModelId: Guid) {
    return this.$http.get<IFlowModelVersionHistory[]>(
      this.APP_CONFIG.apiBaseUrl +
        'modeler/flowmodels/' +
        flowModelId +
        '/versions'
    );
  }

  getFlowModelSchemas(flowModelSchemasIds: Guid[]) {
    return this.$http
      .post<IWorkflowSchemaDetail[]>(
        this.APP_CONFIG.apiBaseUrl + 'modeler/getFlowModelSchemas',
        flowModelSchemasIds
      )
      .then((response) => {
        return response.data;
      });
  }

  getPublishApprovalFlowModel(flowModelId: Guid) {
    return this.$http
      .get<IModelerWorkflowDetail>(
        this.APP_CONFIG.apiBaseUrl + 'modeler/publishApprovals/' + flowModelId
      )
      .then((response) => {
        return response.data;
      });
  }

  checkFlowModelNameExists(name: string, flowModelId?: Guid) {
    ///
    /// Check to see if we have a unique flow model name or not
    ///
    // Encode the name - it will be decoded in the ModelerController on server side.
    const url = this.APP_CONFIG.apiBaseUrl + 'modeler/queryUniqueName';
    return this.$http
      .get(url, {
        params: {
          name: this.validationService.sanitizeString(name),
          ...(flowModelId ? { flowModelId: flowModelId } : {})
        }
      })
      .then((response) => {
        return response.data.DataModel !== true; // Will be true if it is a unique name
      });
  }

  addWorkflow(
    workflow: IFlowModelCommandData | IFlowModelCommandDataPascalCase
  ) {
    return this.$http.put<ICreateFlowModelSummary>(
      this.APP_CONFIG.apiBaseUrl + 'modeler/workflow',
      workflow
    );
  }

  cloneWorkflow(workflow) {
    ///
    /// create a clone of the supplied workflow
    ///
    return this.$http.post(
      this.APP_CONFIG.apiBaseUrl + 'modeler/workflow/clone',
      workflow
    );
  }

  updateWorkflow(
    workflow: IFlowModelCommandDataPascalCase,
    isAutoSave?: boolean
  ) {
    const url = isAutoSave
      ? `${this.APP_CONFIG.apiBaseUrl}modeler/flowmodels/${workflow.Id}?isAutoSave=true`
      : `${this.APP_CONFIG.apiBaseUrl}modeler/flowmodels/${workflow.Id}`;

    return this.$http.post(url, workflow);
  }

  saveFlowModelDetails(workflow) {
    const details = JSON.parse(JSON.stringify(workflow));
    if (details.ProcessDetail && details.ProcessDetail.ProcessReviewDate) {
      details.ProcessDetail.ProcessReviewDate = null;
    }
    return this.$http.post(
      this.APP_CONFIG.apiBaseUrl + 'modeler/saveWorkflowDetails',
      details
    );
  }

  getValidationErrors(schema: string, flowModelId: Guid) {
    return this.$http.post<string[]>(
      this.APP_CONFIG.apiBaseUrl +
        `modeler/flowmodels/${flowModelId}/validationErrors`,
      {
        Schema: schema
      }
    );
  }

  sendTestIntegrationMessage(integrationTestMessage) {
    return this.$http.post(
      this.APP_CONFIG.apiBaseUrl + 'integration/test',
      integrationTestMessage
    );
  }

  async generateFlowModel(command: IGenerateFlowModelCommand): Promise<string> {
    const response = await this.$http.post<string>(
      `${this.APP_CONFIG.apiBaseUrl}modeler/flowmodels/generations`,
      command
    );
    return response.data;
  }

  async linkFlowModelToGenerationFiles(
    flowModelId: Guid,
    generationFileIds: Guid[]
  ): Promise<IHttpResponse<void>> {
    return await this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}modeler/flowmodels/${flowModelId}/generationFilelinks`,
      generationFileIds
    );
  }
}

angular
  .module('flowingly.services')
  .service('modelerApiService', ModelerApiService);

export type ModelerApiServiceType = InstanceType<typeof ModelerApiService>;
